import { useState, type FormEvent } from 'react';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { Button, ErrorState } from '@helios/ui';
import { useAuth } from '@/features/auth/authContext';
import { useSessionScopeFence } from '@/hooks/useSessionScope';
import { useAiKnowledge, useAiKnowledgeMutations } from './aiSettings.hooks';
import type { AiKnowledgeEntry } from './aiSettings.types';
import { SettingsDraftActions } from './SettingsDraftActions';
import {
  SettingsField,
  SettingsLoadingState,
  SettingsMutationBanner,
  SettingsSection,
  settingsControlClass,
} from './settingsChrome';
import { useSettingsDraft } from './useSettingsDraft';

const scopeLabels = { all: 'All Helios contexts', helios: 'Helios assistant' };
const emptyEntry = {
  title: '',
  content: '',
  category: 'general',
  scope: 'all' as AiKnowledgeEntry['scope'],
};

export function AiKnowledgeSettings() {
  const query = useAiKnowledge();
  const mutations = useAiKnowledgeMutations();
  const auth = useAuth();
  const fence = useSessionScopeFence(PERMISSIONS.SETTINGS_MANAGE);
  const draft = useSettingsDraft(
    query.data ? { form: emptyEntry, version: query.data.version } : null,
  );
  const [view, setView] = useState<{
    scope: string;
    search: string;
    category: string;
    agentScope: string;
    expanded: string | null;
  } | null>(null);
  const [notice, setNotice] = useState<{ scope: string; message: string; error?: Error } | null>(
    null,
  );
  const currentView =
    view?.scope === draft.scope
      ? view
      : { scope: draft.scope, search: '', category: 'all', agentScope: 'any', expanded: null };
  const updateView = (patch: Partial<typeof currentView>) => setView({ ...currentView, ...patch });
  const canManage = Boolean(
    query.data?.canManage &&
      fence.enabled &&
      hasPermission(auth.permissions, PERMISSIONS.SETTINGS_MANAGE),
  );
  const entries = query.data?.entries ?? [];
  const search = currentView.search.trim().toLocaleLowerCase();
  const filtered = entries.filter(
    (entry) =>
      (currentView.category === 'all' || entry.category === currentView.category) &&
      (currentView.agentScope === 'any' || entry.scope === currentView.agentScope) &&
      `${entry.title} ${entry.category} ${entry.content}`.toLocaleLowerCase().includes(search),
  );
  const busy = mutations.create.isPending || mutations.remove.isPending;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canManage || busy || !draft.form || draft.version === null) return;
    const origin = fence.identity;
    try {
      const saved = await mutations.create.mutateAsync({
        expectedVersion: draft.version,
        ...draft.form,
      });
      if (!fence.isCurrent(origin)) return;
      draft.accept({ form: emptyEntry, version: saved.version });
      setNotice({ scope: draft.scope, message: 'Knowledge entry added.' });
    } catch (error) {
      if (fence.isCurrent(origin))
        setNotice({
          scope: draft.scope,
          message: '',
          error: error instanceof Error ? error : new Error('Knowledge entry could not be added.'),
        });
    }
  };
  const remove = async (entry: AiKnowledgeEntry) => {
    if (!canManage || busy || !query.data) return;
    const origin = fence.identity;
    try {
      await mutations.remove.mutateAsync({ id: entry.id, expectedVersion: query.data.version });
      if (fence.isCurrent(origin))
        setNotice({ scope: draft.scope, message: 'Knowledge entry removed.' });
    } catch (error) {
      if (fence.isCurrent(origin))
        setNotice({
          scope: draft.scope,
          message: '',
          error:
            error instanceof Error ? error : new Error('Knowledge entry could not be removed.'),
        });
    }
  };
  if (query.isLoading) return <SettingsLoadingState label="Loading AI knowledge…" />;
  if (!query.data)
    return (
      <ErrorState
        title="AI knowledge unavailable"
        description="The workspace reference library could not be loaded."
        action={
          <Button size="xs" variant="outline" onClick={() => void query.refetch()}>
            Retry knowledge
          </Button>
        }
      />
    );
  return (
    <SettingsSection
      title="Workspace reference library"
      description="Approved reference material for Helios. Entries are treated as source data."
      actions={
        <>
          <span className="text-2xs text-muted-foreground">
            {entries.length}/200 entries · version {query.data.version}
          </span>
          <Button
            size="xs"
            variant="outline"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Refresh knowledge
          </Button>
        </>
      }
    >
      <div className="grid gap-2.5">
        {query.isError ? (
          <p className="text-warning text-xs" role="status">
            Refresh failed. Showing the last loaded knowledge entries.
          </p>
        ) : null}
        <SettingsMutationBanner
          message={notice?.scope === draft.scope ? notice.message : null}
          error={notice?.scope === draft.scope ? notice.error : null}
        />
        <div className="grid gap-2 sm:grid-cols-3">
          <SettingsField label="Search knowledge">
            <input
              type="search"
              className={settingsControlClass}
              value={currentView.search}
              onChange={(event) => updateView({ search: event.target.value })}
              placeholder="Title, category, or content"
            />
          </SettingsField>
          <SettingsField label="Category">
            <select
              className={settingsControlClass}
              value={currentView.category}
              onChange={(event) => updateView({ category: event.target.value })}
            >
              <option value="all">All categories</option>
              {[...new Set(entries.map((entry) => entry.category))].sort().map((category) => (
                <option key={category} value={category}>
                  {category.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsField label="Agent scope">
            <select
              className={settingsControlClass}
              value={currentView.agentScope}
              onChange={(event) => updateView({ agentScope: event.target.value })}
            >
              <option value="any">Any scope</option>
              {Object.entries(scopeLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </SettingsField>
        </div>
        <div className="divide-border divide-y">
          {filtered.map((entry) => (
            <div key={entry.id} className="flex items-start justify-between gap-3 py-2">
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  className="text-foreground text-left text-xs font-medium hover:underline"
                  aria-expanded={currentView.expanded === entry.id}
                  onClick={() =>
                    updateView({ expanded: currentView.expanded === entry.id ? null : entry.id })
                  }
                >
                  {entry.title}
                </button>
                <p className="text-muted-foreground text-2xs">
                  {entry.category.replaceAll('_', ' ')} · {scopeLabels[entry.scope]}
                </p>
                {currentView.expanded === entry.id ? (
                  <p className="text-foreground mt-1 whitespace-pre-wrap break-words text-xs">
                    {entry.content}
                  </p>
                ) : null}
              </div>
              {canManage ? (
                <Button
                  size="xs"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void remove(entry)}
                  aria-label={`Delete ${entry.title}`}
                >
                  Delete
                </Button>
              ) : null}
            </div>
          ))}
          {filtered.length === 0 ? (
            <p className="text-muted-foreground py-3 text-xs">
              {entries.length
                ? 'No entries match your filters.'
                : 'No knowledge entries yet. Add workspace policies, procedures, and source guidance.'}
            </p>
          ) : null}
        </div>
        {canManage && draft.form ? (
          <form
            onSubmit={(event) => void submit(event)}
            className="border-border grid gap-2 border-t pt-2.5"
          >
            <h3 className="text-foreground text-xs font-semibold">Add knowledge entry</h3>
            <SettingsDraftActions
              dirty={draft.dirty}
              changedOnServer={draft.changedOnServer}
              onRevert={() => {
                draft.accept();
                setNotice(null);
              }}
              disabled={busy}
            />
            <div className="grid gap-2 sm:grid-cols-3">
              <SettingsField label="Knowledge title">
                <input
                  className={settingsControlClass}
                  value={draft.form.title}
                  required
                  minLength={3}
                  maxLength={200}
                  disabled={busy}
                  onChange={(event) => draft.change({ ...draft.form!, title: event.target.value })}
                />
              </SettingsField>
              <SettingsField label="Entry category">
                <input
                  className={settingsControlClass}
                  value={draft.form.category}
                  required
                  maxLength={60}
                  pattern={'[a-z0-9][a-z0-9_\\-]{0,59}'}
                  disabled={busy}
                  onChange={(event) =>
                    draft.change({ ...draft.form!, category: event.target.value })
                  }
                />
              </SettingsField>
              <SettingsField label="Entry agent scope">
                <select
                  className={settingsControlClass}
                  value={draft.form.scope}
                  disabled={busy}
                  onChange={(event) =>
                    draft.change({
                      ...draft.form!,
                      scope: event.target.value as AiKnowledgeEntry['scope'],
                    })
                  }
                >
                  {Object.entries(scopeLabels).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </SettingsField>
            </div>
            <SettingsField label="Knowledge content">
              <textarea
                className={`${settingsControlClass} min-h-24 resize-y rounded-lg py-2`}
                value={draft.form.content}
                required
                minLength={10}
                maxLength={20_000}
                disabled={busy}
                onChange={(event) => draft.change({ ...draft.form!, content: event.target.value })}
              />
            </SettingsField>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                size="xs"
                disabled={busy || entries.length >= 200 || !draft.dirty}
              >
                {mutations.create.isPending ? 'Adding…' : 'Add knowledge entry'}
              </Button>
              <span className="text-muted-foreground text-2xs">
                Up to 200 entries. Only permitted sources are supplied to assistants.
              </span>
            </div>
          </form>
        ) : (
          <p className="text-muted-foreground text-xs">
            Workspace settings permission is required to add or remove reference material.
          </p>
        )}
      </div>
    </SettingsSection>
  );
}
